import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_WHY_THIS_RESULT } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

export const customerBaseCardFixtures: Record<string, IntelligenceFixture> = {
  customer_population_activity: {
    cardKey: 'customer_population_activity',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    breakdownKey: 'customer_segment_distribution',
    metrics: [
      // Labels and figures are the screens' own, measured off node 589:29429. The set closes on
      // itself: 64,800 eligible + 17,650 excluded = 82,450 registered (the 78.6% ring), and 46,915
      // active of 82,450 = 56.9% (the second ring and `activity_rate` below).
      //
      // The two `pp` secondaries below are the export's own 3.8% and 5.1%. Neither reconciles
      // against its count (+1,150 on 82,450 is 1.41%, +1,250 on 46,915 is 2.74%), but these are mock
      // values standing in for a Metabase reading, so they reproduce the screen rather than a
      // recomputation of it — raised with the designer instead. PLA-8815.
      {
        key: 'total_customers',
        label: 'Total Registered Users',
        value: 82450,
        format: 'count',
        delta: {
          value: 1150,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 3.8, unit: 'pp' }
        }
      },
      {
        key: 'active_customers',
        label: 'Active Users',
        value: 46915,
        format: 'count',
        denominator: 'of 82,450 total registered users',
        delta: {
          value: 1250,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 5.1, unit: 'pp' }
        }
      },
      {
        key: 'activity_rate',
        label: 'Active User Rate',
        value: 56.9,
        format: 'percent',
        delta: { value: 2.4, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      // 78.6%, Ring 1's own sweep angle in the removed coverage ring's export — 282.96deg / 360deg —
      // not a count recomputed from this card's other tiles (design/section-customer-base-svg-diff-
      // 2026-08-11.md § the two-ring table; Ring 2 there is 204.84deg = 56.9%, `activity_rate` above,
      // confirming both rings are this card's). Live, this tile is O-COM-001's `eligible_customer_count`
      // over its own `metric_value`; the fixture states the same reading the export drew rather than a
      // count this file invents. No delta: the live tile has none either — a period-over-period
      // comparison of a quotient is not derivable from the one row the ratio is taken from. PLA-8815.
      {
        key: 'eligible_coverage_rate',
        label: 'Eligible Coverage',
        value: 78.6,
        format: 'percent'
      },
      // The legend readings beside the rings, off the same export. The set still closes on itself:
      // 64,800 + 17,650 = 82,450. Live, these are O-COM-001's `eligible_customer_count` and that
      // count taken out of its own `metric_value`. No delta on either: the design shows none, and
      // none is derivable from the single row each is taken from. PLA-8815.
      {
        key: 'eligible_customers',
        label: 'Total Eligible Customers',
        value: 64800,
        format: 'count'
      },
      {
        key: 'ineligible_customers',
        label: 'Ineligible / excluded',
        value: 17650,
        format: 'count'
      },
      // NOT 46,915 of 64,800 (72.4%) — that reads as "share of the eligible cohort that is active",
      // which needs the INTERSECTION of the two cohorts, a joint count this contract does not carry
      // (see `registry/metricIdentifiers.ts`'s comment on this same key). `unmapped`, so this fixture
      // states the same `value: null` `assembleCard` would, rather than a number the tile cannot
      // actually mean. PLA-8815.
      {
        key: 'eligible_active_rate',
        label: 'Eligible active rate',
        value: null,
        format: 'percent',
        notConfigured: true
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      reasons: ['Full transaction coverage for the selected range', 'Identity resolution above 95%']
    },
    whyThisResult: 'Activity is measured on customers with at least one qualifying event in range.'
  },

  data_trust_integration_health: {
    cardKey: 'data_trust_integration_health',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // `progress` is the gauge fill read off the scene graph
    // (design/card-data-trust-integration-health-devmode-2026-08-12.md § 2), not a transform of the
    // reading. There is no single rule: the first three bars ARE their value (96/92/88), Integration
    // Health maps the word `Healthy` to 98, and the reversal rate maps 1.8 to 18 by arithmetic the
    // file does not show. The last one is a data question, not a design one — UNVERIFIED.
    metrics: [
      {
        key: 'data_freshness_score',
        label: 'Data Freshness Score (DFS)',
        value: 96,
        format: 'points',
        delta: { value: 2.0, unit: 'points', direction: 'up', sentiment: 'positive' },
        progress: 0.96,
        progressTone: 'positive'
      },
      {
        key: 'source_coverage_score',
        label: 'Source Coverage Score (SCS)',
        value: 92,
        format: 'points',
        delta: { value: 2.0, unit: 'points', direction: 'up', sentiment: 'positive' },
        progress: 0.92,
        progressTone: 'info'
      },
      {
        key: 'identity_confidence',
        label: 'Identity Confidence',
        value: 88,
        format: 'percent',
        delta: { value: 2.0, unit: 'pp', direction: 'up', sentiment: 'positive' },
        progress: 0.88,
        progressTone: 'accent'
      },
      {
        key: 'integration_health_status',
        label: 'Integration Health Status',
        // Categorical: the reading is the word, so there is no figure to carry and `null` here is
        // not the "no reading" case `MetricValue` draws a placeholder for.
        value: null,
        valueText: 'Healthy',
        format: 'points',
        status: { label: 'Within SLA', tone: 'positive' },
        progress: 0.98,
        progressTone: 'positive'
      },
      {
        key: 'reversal_failure_rate',
        label: 'Reversal Failure / Retry Rate',
        value: 1.8,
        format: 'percent',
        // The screen paints this rise in the positive hue, which would read a worsening failure rate
        // as an improvement. `sentiment` exists to be decoupled from `direction` for exactly this, so
        // the tone follows the business meaning and the arrow still follows the movement.
        delta: { value: 0.2, unit: 'pp', direction: 'up', sentiment: 'negative' },
        progress: 0.18,
        progressTone: 'warning'
      }
    ],
    sourceEvidence: {
      label: 'SOURCE & SYNC EVIDENCE',
      sources: [
        {
          label: 'Transactions',
          latency: '4 min',
          detail: 'Healthy · 99.8% accepted',
          tone: 'positive'
        },
        {
          label: 'Campaigns',
          latency: '8 min',
          detail: 'Healthy · within 15-min SLA',
          tone: 'positive'
        },
        { label: 'CRM', latency: '3 hr', detail: 'Delayed · 1 source warning', tone: 'warning' },
        {
          label: 'Rewards',
          latency: '6 min',
          detail: 'Healthy · 0.3% error rate',
          tone: 'positive'
        }
      ],
      /* PLA-8828 FE-C13: 'Reversal retries pending resolution' removed — a Product-rejected concept.
         This is a FIXTURE, not the demo layer, so it is NOT removed when DEMO_FALLBACK_ENABLED goes
         off; a grep gate scoped to `demoFallback*` would have passed while this one still shipped. */
      alerts: [{ label: 'Identity conflicts requiring review', value: '1,126 · 1.4%' }]
    },
    confidence: {
      score: 88,
      band: 'high',
      // This card publishes its score without a `Reason:` line; the other cards keep theirs. Empty
      // rather than absent because the badge still renders and the field is not optional.
      reasons: []
    }
  },

  customer_lifecycle_holdings: {
    cardKey: 'customer_lifecycle_holdings',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // The KPI row is three tiles, not the lifecycle counts: the screen puts those in the stacked bar
    // and heads the card with a different set entirely (design § 3).
    metrics: [
      {
        key: 'total_eligible_customers',
        label: 'Total Eligible Customers',
        // The screen draws this tile in an empty state — `Not Configured` beside an icon, where the
        // other two carry a figure. That is a designed state, so the words are the reading; the
        // design supplies no example figure and inventing one is the only way this could hold a
        // number. Same shape as Integration Health above.
        value: null,
        valueText: 'Not Configured',
        format: 'count'
      },
      {
        key: 'average_active_holdings',
        label: 'Average Active Holdings',
        value: 1.8,
        format: 'count',
        delta: { value: 0.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      // Back to the design's own authored 41% share: O-COM-032 bands each eligible customer exactly
      // once, so its segment counts state the denominator this tile was thought to be missing and
      // the live screen now draws a share too (`registry/metricIdentifiers.ts` § this key). A `pp`
      // movement, not a `count` one -- a share moves in percentage points. PLA-8815.
      {
        key: 'multi_product_customers',
        label: 'Multi-product Customers',
        value: 41,
        format: 'percent',
        delta: { value: 3.2, unit: 'pp', direction: 'down', sentiment: 'negative' }
      }
    ],
    // No `CALCULATION & EVIDENCE` block: the screen does not draw one on this card. It draws two
    // whyboxes instead, `Why this result?` and `Evidence`, both with placeholder bodies.
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT,
    evidenceNote: PLACEHOLDER_WHY_THIS_RESULT
  }
};

// The distributions the design draws below the metric row. `IntelligenceFixture` has no field for
// them and no endpoint shape exists yet to model one against, so they sit beside the payload they
// decorate; the section supplies colour, share and formatting, which are presentation, not data.
//
// `lifecycleStages` totals the lifecycle card's own 64,800, which is also the population card's
// `eligible_customers`. The two cards therefore count different populations — 82,450 registered
// against 64,800 eligible — which is the screens' own arithmetic, not a drift introduced here.
//
// The two ring segment lists that used to live here are gone: both rings are drawn from the card's
// own live rates now (`activity_rate`, `eligible_coverage_rate`), so the only thing left for this
// export to carry is the copy the design writes rather than measures. PLA-8815.
export const customerBaseVisuals = {
  // The definition each ring is read against, drawn in the sub-panel under it. Split so the term
  // carries the screen's emphasis without the section having to parse the sentence for it.
  coverageFormula: {
    term: 'Eligible coverage',
    body: '= eligible customers ÷ total registered users × 100'
  },
  activityFormula: {
    term: 'Active User Rate',
    body: '= active users ÷ total registered users × 100'
  },
  /* The third row of each ring legend (`589-29436`). Only the LABELS live here. `Snapshot`'s figure
     is a date and therefore a reading, so it is taken from the payload's own `updatedAt` in the
     section rather than transcribed off the design — a typed-in date would be indistinguishable from
     a measured one, which is the whole failure this seam exists to prevent. `Counting`'s figure is a
     counting RULE, the same class of thing as the formula captions above, so it is copy. PLA-8815. */
  coverageSnapshotLabel: 'Snapshot',
  activityCounting: { label: 'Counting', value: 'Once / period' },
  // Six stages, counts and definitions verbatim off the stacked bar and its legend (design § 4b/4c).
  // They total the card's own 64,800. The bar is drawn to these VALUES, not to the export's segment
  // widths: those sum to 919px inside an 828px stack and paint 4% wider than 12%, so they are a file
  // error rather than a layout to copy.
  //
  // `Live` and `At-risk` are the screen's names for what we called `New` and `Low-engaged`, and the
  // definitions are not synonyms — `newly registered, never previously active` is a narrower claim
  // than `New`. The metric keys follow the screen. PLA-8815.
  //
  // `key` is O-COM-031's own `distribution_key` for the stage, which is what joins this row of copy
  // to the live share the bar is drawn at. A governed vocabulary, not a slug of the label: the
  // screen's `Live` is the catalogue's `new`, so deriving one from the other would silently drop
  // that stage. `value` is the design's own example count and is no longer drawn — the bar is drawn
  // from the live shares; it stays as the measurement the definitions were authored against.
  lifecycleStages: [
    {
      key: 'active',
      label: 'Active',
      value: 34992,
      definition: 'Active: qualifying activity in current month'
    },
    {
      key: 'at_risk',
      label: 'At-risk',
      value: 10368,
      definition: 'At-risk: 1–2 inactive months or risk threshold'
    },
    { key: 'dormant', label: 'Dormant', value: 7776, definition: 'Dormant: ≥3 inactive months' },
    {
      key: 'churned',
      label: 'Churned',
      value: 5184,
      definition: 'Churned: closed/terminated or 6 inactive months'
    },
    {
      key: 'new',
      label: 'Live',
      value: 3888,
      definition: 'Live: newly registered, never previously active'
    },
    {
      key: 'reactivated',
      label: 'Reactivated',
      value: 2592,
      definition: 'Reactivated: active now after ≥3 inactive months'
    }
  ],
  // Shares, not counts: the screen prints a percentage per row and no count anywhere, so a count
  // here would be invented. Every fill is exactly its percentage of the track in the export, which
  // is what `RankedBarList` already derives.
  // The inset explainer under the holdings bars (`589:32392` / `589:32393`). Real authored copy, not
  // a placeholder — the earlier read only had the layer name, which truncated it. Term and body are
  // one text node with a bold span, so they split the same way the card-1 formula panels do.
  holdingFormula: {
    term: 'Holding distribution',
    body:
      'Distinct eligible customers with each active-holding count ÷ distinct eligible customers × 100. ' +
      'Each holding is active on the selected end date.'
  },
  // `key` is O-COM-032's own `distribution_key` per band. Its response states the band contract on
  // every row — `DA3 active-holding band contract {0,1,2,3,4+}` — and those five bands are exactly
  // the five the design draws, in this order. `pct` is the design's example share; the ranked list
  // is drawn from the live band shares, and a band the response omits is not drawn at all.
  productHoldings: [
    { key: '0', label: 'No holding', pct: 0.09 },
    { key: '1', label: '1 product', pct: 0.5 },
    { key: '2', label: '2 products', pct: 0.28 },
    { key: '3', label: '3 products', pct: 0.1 },
    { key: '4+', label: '4+ products', pct: 0.03 }
  ]
};
